/**
 * iter76 — Central hook returning the LIVE list of storefront-visible
 * categories from the backend.  Filters out soft-deleted categories and
 * respects the admin-defined order.  Every consumer (Shop filter chips,
 * SearchOverlay, admin Product Editor, admin Combo Editor, Homepage
 * category grid, etc.) subscribes to this single source of truth so a
 * single Admin → Categories change propagates everywhere.
 */
import { useEffect, useState } from "react";
import api from "../lib/api";

// Bundled fallback order — used only before the first API response
// resolves so no consumer ever sees an empty list on first paint.
const FALLBACK = [
  "Pickles", "Culinary Pastes", "Spices", "Masalas",
  "Traditional Powders", "Flours", "Wood Pressed Oils", "Combos",
];

function seedCategories() {
  return FALLBACK.map((n, i) => ({
    id: n.toLowerCase().replace(/\s+/g, "-"),
    name: n,
    order: i,
  }));
}

/** Returns just the array of categories (fallback while loading, live
 *  once the API responds). */
export function useCategories() {
  const { categories } = useCategoriesWithMeta();
  return categories;
}

/** iter79 — Returns `{categories, loaded}` so callers that need to
 *  distinguish "still fetching" from "server responded" (e.g. Shop's
 *  unknown-slug redirect guard) can gate on `loaded` explicitly and
 *  avoid false-positives from the FALLBACK seed data. */
export function useCategoriesWithMeta() {
  const [categories, setCategories] = useState(seedCategories);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let live = true;
    api.get("/categories").then((r) => {
      if (!live) return;
      if (Array.isArray(r.data) && r.data.length > 0) setCategories(r.data);
    }).catch(() => {}).finally(() => {
      if (live) setLoaded(true);
    });
    return () => { live = false; };
  }, []);
  return { categories, loaded };
}
